/**
 * 指挥员定位（从 Dashboard 拆出）
 */
import mapboxgl from 'mapbox-gl';
import { isSecureGeolocationContext, getGeolocationHttpsHint } from '../secureContext';
import { toMapLngLat } from './basemapCoord';
import { calculateZoomForHeight, COMMAND_VIEW_HEIGHT_METERS } from './mapFlyHelpers';
import {
  isBogusGpsCoordinate,
  isPlausibleCommandCoordinate,
  purgeBogusMapLocalState
} from '../bogusGps';

const LAST_COMMANDER_LOCATION_KEY = 'dashboard_last_commander_location';

function readProjectionName(map) {
  try {
    return map?.getProjection?.()?.name || 'mercator';
  } catch (_) {
    return 'mercator';
  }
}
export { COMMAND_VIEW_HEIGHT_METERS };
/** 构造「帐号用定位工具留下的指挥员位置」记录 */
export function buildCommanderLocationRecord({
  lng,
  lat,
  accuracy = null,
  username = ''
} = {}) {
  const longitude = Number(lng);
  const latitude = Number(lat);
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null;
  const now = Date.now();
  return {
    lng: longitude,
    lat: latitude,
    accuracy: Number.isFinite(Number(accuracy)) ? Number(accuracy) : null,
    username: username || '',
    locatedAt: now,
    locatedAtRaw: new Date(now).toISOString()
  };
}

export function readLastCommanderLocationFromStorage(
  username = '',
  storage = typeof localStorage !== 'undefined' ? localStorage : null
) {
  if (!storage) return null;
  try {
    purgeBogusMapLocalState(storage);
    const raw = storage.getItem(LAST_COMMANDER_LOCATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Number.isFinite(Number(parsed?.lng)) || !Number.isFinite(Number(parsed?.lat))) return null;
    if (!isPlausibleCommandCoordinate(parsed.lat, parsed.lng)) {
      storage.removeItem(LAST_COMMANDER_LOCATION_KEY);
      console.warn('忽略境外/伪定位指挥员存档', parsed.lat, parsed.lng);
      return null;
    }
    // 若传入用户名，优先匹配该帐号；旧记录无 username 时仍可用
    if (username && parsed.username && parsed.username !== username) return null;
    return {
      ...parsed,
      lng: Number(parsed.lng),
      lat: Number(parsed.lat),
      accuracy: Number.isFinite(Number(parsed.accuracy)) ? Number(parsed.accuracy) : null,
      locatedAt: Number.isFinite(Number(parsed.locatedAt)) ? Number(parsed.locatedAt) : null
    };
  } catch (_) {
    return null;
  }
}

export function writeLastCommanderLocationToStorage(
  record,
  storage = typeof localStorage !== 'undefined' ? localStorage : null
) {
  if (!storage || !record) return;
  if (!isPlausibleCommandCoordinate(record.lat, record.lng)) {
    console.warn('拒绝写入境外/伪定位指挥员坐标', record.lat, record.lng);
    try {
      storage.removeItem(LAST_COMMANDER_LOCATION_KEY);
    } catch (_) {
      /* ignore */
    }
    return;
  }
  try {
    storage.setItem(LAST_COMMANDER_LOCATION_KEY, JSON.stringify(record));
  } catch (_) {
    /* ignore */
  }
}

/** 飞到指挥员上次定位坐标，并恢复 🎯 标记 */
export function flyToLastCommanderLocation(map, record, options = {}) {
  if (!map || !record) return false;
  const lng = Number(record.lng);
  const lat = Number(record.lat);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false;
  if (!isPlausibleCommandCoordinate(lat, lng)) {
    console.warn('拒绝飞向境外/伪定位指挥员坐标', lat, lng);
    try {
      purgeBogusMapLocalState();
    } catch (_) {
      /* ignore */
    }
    return false;
  }

  // 显式 basemapId，避免过期的 map.__zhxtBasemapId 误纠偏
  const basemapId = options.basemapId;
  const display = toMapLngLat(lng, lat, basemapId, map) || [lng, lat];

  try {
    // 停掉进行中的 jumpTo/flyTo（地球自转），否则飞行动画会被打断成「瞬移」
    map.stop?.();
  } catch (_) {
    /* ignore */
  }

  try {
    placeCommanderMarker(map, display[0], display[1], record.accuracy, { wgsLng: lng, wgsLat: lat });
  } catch (error) {
    console.warn('恢复指挥员标记失败（仍继续飞行）:', error);
  }

  // 从地球俯冲时保持 globe，与首页一致的缓动；默认落到约 600m 离地
  const fromEarth = options.fromEarthOverview === true
    || (typeof map.getZoom === 'function' && Number(map.getZoom()) <= 2.5);
  const screenH = (typeof window !== 'undefined' && map.getContainer?.()?.clientHeight)
    || (typeof window !== 'undefined' ? window.innerHeight : 800);
  const zoomForHeight = calculateZoomForHeight(
    options.heightMeters ?? COMMAND_VIEW_HEIGHT_METERS,
    lat,
    screenH
  );
  const duration = options.duration ?? (fromEarth ? 3200 : 3000);
  const easing = options.easing || (fromEarth ? ((t) => t * (2 - t)) : undefined);

  map.flyTo({
    center: display,
    zoom: options.zoom ?? zoomForHeight,
    duration,
    essential: true,
    ...(easing ? { easing } : {})
  });

  if (fromEarth && typeof options.onEarthDiveScheduled === 'function') {
    options.onEarthDiveScheduled(map);
  }
  return true;
}

export function createCommanderMarkerElement() {
  const commanderMarkerEl = document.createElement('div');
  commanderMarkerEl.innerHTML = '🎯';
  commanderMarkerEl.style.cssText = `
                          width: 40px;
                          height: 40px;
                          font-size: 30px;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
                        `;
  const pulseEl = document.createElement('div');
  pulseEl.style.cssText = `
                          position: absolute;
                          width: 60px;
                          height: 60px;
                          border: 3px solid #ff6b6b;
                          border-radius: 50%;
                          top: -10px;
                          left: -10px;
                          animation: pulse 2s ease-out infinite;
                          pointer-events: none;
                        `;
  commanderMarkerEl.appendChild(pulseEl);
  return commanderMarkerEl;
}

export function placeCommanderMarker(map, longitude, latitude, accuracy, meta = {}) {
  if (!map) return null;
const projection = readProjectionName(map);
  if (projection === 'globe') {
    return null;
  }

  if (typeof window !== 'undefined' && window.commanderMarker) {
    try {
      window.commanderMarker.remove();
    } catch (_) {
      /* ignore */
    }
  }
  // popup 展示业务 WGS84；marker 使用已转换的显示坐标
  const showLng = Number.isFinite(Number(meta.wgsLng)) ? Number(meta.wgsLng) : Number(longitude);
  const showLat = Number.isFinite(Number(meta.wgsLat)) ? Number(meta.wgsLat) : Number(latitude);
  const el = createCommanderMarkerElement();
  const marker = new mapboxgl.Marker(el)
    .setLngLat([longitude, latitude])
    .setPopup(new mapboxgl.Popup().setHTML(`
                            <div style='min-width:180px; font-size:14px; color:#222;'>
                              <div><b>指挥员位置</b></div>
                              <div><b>经度：</b>${showLng.toFixed(6)}</div>
                              <div><b>纬度：</b>${showLat.toFixed(6)}</div>
                              <div><b>精度：</b>${accuracy ? accuracy.toFixed(1) + '米' : '未知'}</div>
                              <div><b>定位时间：</b>${new Date().toLocaleString()}</div>
                            </div>
                          `))
    .addTo(map);
  if (typeof window !== 'undefined') window.commanderMarker = marker;
  return marker;
}

export function geolocationErrorMessage(error) {
  if (!error) return '获取位置时发生未知错误';
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return '位置权限被拒绝，请在浏览器设置中允许位置访问';
    case error.POSITION_UNAVAILABLE:
      return '位置信息不可用，请检查GPS是否开启';
    case error.TIMEOUT:
      return '获取位置超时，请重试';
    default:
      return '获取位置时发生未知错误';
  }
}

/**
 * 定位指挥员并飞到当前位置
 * @param {object} options
 * @param {() => any} options.getMap
 * @param {{ loading?: Function, success?: Function, error?: Function, destroy?: Function }} options.ui
 * @param {number} [options.zoom]
 * @param {string} [options.basemapId] 当前底图 id（用于 GCJ 纠偏，务必传入）
 * @param {() => void} [options.beforeFly] 飞行前钩子（停地球自转等）
 * @param {string} [options.username] 当前登录帐号，用于按帐号记住定位
 * @param {(record: object) => void} [options.onLocated] 定位成功回调（已写入存储前/后均可）
 */
export function locateCommanderOnMap(options = {}) {
  const {
    getMap,
    ui = {},
    zoom = 14.5,
    duration = 2000,
    basemapId,
    beforeFly,
    username = '',
    onLocated
  } = options;

  if (!navigator.geolocation) {
    ui.error?.('您的浏览器不支持地理定位功能');
    return;
  }
  if (!isSecureGeolocationContext()) {
    ui.error?.(getGeolocationHttpsHint());
    return;
  }

  ui.loading?.('正在获取位置信息...');

  navigator.geolocation.getCurrentPosition(
    (position) => {
      ui.destroy?.();
      const { longitude, latitude, accuracy } = position.coords;
      if (isBogusGpsCoordinate(latitude, longitude) || !isPlausibleCommandCoordinate(latitude, longitude)) {
        console.warn('浏览器定位返回不可用坐标，已忽略', latitude, longitude);
        purgeBogusMapLocalState();
        ui.error?.('定位结果不在可用范围（疑似网络占位点）。请开启 GPS/精准位置后重试，或点左侧队员定位。');
        return;
      }
      const map = getMap?.();
      if (!map) {
        ui.error?.('地图未就绪');
        return;
      }
      const record = buildCommanderLocationRecord({
        lng: longitude,
        lat: latitude,
        accuracy,
        username: username || (typeof localStorage !== 'undefined' ? localStorage.getItem('username') : '') || ''
      });
      if (record) {
        writeLastCommanderLocationToStorage(record);
        onLocated?.(record);
      }

      // 先停自转/动画，再算显示坐标并只 fly 一次（避免先偏后跳）
      try {
        beforeFly?.();
      } catch (_) {
        /* ignore */
      }
      try {
        map.stop?.();
      } catch (_) {
        /* ignore */
      }

      const display = toMapLngLat(longitude, latitude, basemapId, map) || [longitude, latitude];
      placeCommanderMarker(map, display[0], display[1], accuracy, {
        wgsLng: longitude,
        wgsLat: latitude
      });
      map.flyTo({
        center: display,
        zoom,
        duration,
        essential: true
      });
      ui.success?.(`定位成功！精度: ${accuracy ? `${accuracy.toFixed(1)}米` : '未知'}`);
    },
    (error) => {
      ui.destroy?.();
      console.error('获取位置失败:', error);
      ui.error?.(geolocationErrorMessage(error));
    },
    {
      enableHighAccuracy: true,
      timeout: 15000,
      // 禁止使用浏览器缓存的旧网络定位（Chrome 常缓存 MaxMind 堪萨斯点）
      maximumAge: 0
    }
  );
}

export function validatePlaybackRange(playbackRange) {
  if (!playbackRange?.start || !playbackRange?.end) {
    return { ok: false, message: '请选择开始时间和结束时间' };
  }
  const start = Number(playbackRange.start?.valueOf?.() ?? playbackRange.start);
  const end = Number(playbackRange.end?.valueOf?.() ?? playbackRange.end);
  if (!(start < end)) {
    return { ok: false, message: '开始时间必须早于结束时间' };
  }
  return { ok: true };
}

/**
 * 定位到当前选中队员，若无则定位第一个在线队员
 */
export function flyToActiveOrOnlineDevice(options = {}) {
  const {
    getMap,
    members = [],
    activeMemberId = null,
    onlineTimeout = 5 * 60 * 1000,
    zoom = 16.72,
    duration = 2000,
    alertFn = typeof alert === 'function' ? alert : () => {}
  } = options;

  const map = getMap?.();
  const fly = (member) => {
    if (!map || !member) return false;
    try {
      options.stopEarthAnimation?.();
    } catch (_) {
      /* ignore */
    }
    try {
      map.stop?.();
    } catch (_) {
      /* ignore */
    }
    const center = toMapLngLat(
      member.last_longitude,
      member.last_latitude,
      options.basemapId,
      map
    ) || [member.last_longitude, member.last_latitude];
    map.flyTo({
      center,
      zoom,
      duration
    });
    return true;
  };

  if (activeMemberId) {
    const activeMember = members.find((m) => m.device_unique_id === activeMemberId);
    if (activeMember?.last_longitude && activeMember?.last_latitude) {
      fly(activeMember);
      return { ok: true, member: activeMember };
    }
    alertFn('当前队员暂无有效位置信息');
    return { ok: false, reason: 'active-invalid' };
  }

  const now = Date.now();
  const onlineMember = members.find((m) => {
    const lastSeen = m.last_seen ? new Date(m.last_seen).getTime() : 0;
    return !Number.isNaN(lastSeen) && (now - lastSeen) < onlineTimeout;
  });

  if (onlineMember?.last_longitude && onlineMember?.last_latitude) {
    fly(onlineMember);
    return { ok: true, member: onlineMember };
  }

  alertFn('没有找到在线队员的位置信息');
  return { ok: false, reason: 'no-online' };
}
